import { colors2, getVal } from "oolib";
import React, { CSSProperties, useState, useMemo, useCallback, useEffect } from "react";
import {
  ResponsiveContainer,
  PieChart as ReChartsPie,
  Pie,
  Cell,
  Sector,
  Tooltip,
} from "recharts";
import styled from "styled-components";

import { Legend } from "../Legend";
import TooltipV2 from "@/components/UI/Tooltip";
import { TooltipContent } from "../CustomTooltip";

import { usePrepareData } from "../utils/usePrepareData";
import { useLegendOrientation } from "../utils/useLegendOrientation";
import { useTrackMousePosition } from "../utils/useTrackMousePosition";

import { dataVizColors } from "../common/colors";
import { VizArea, VizPanel, VizShell } from "../common/VizShell";
import { SidePanel } from "@/components/UI/SidePanel";
import { usePieChartData, MultiLevelPieData } from "./utils/usePieChartData";
import { generateChildColors, generateColorsForAllLevels } from "./utils/colorUtils";
import { usePieChartSizing } from "./utils/usePieChartSizing";
import { _base_piechart_ui_configs, calculateRadiusRanges } from "./_base_piechart_ui_configs";

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const StyledPieChartContainer = styled.div`
  svg:focus,
  g:focus,
  path:focus {
    outline: none;
  }
`;

// =============================================================================
// MEMOIZED TOOLTIP CONTENT COMPONENT
// =============================================================================
// This component handles its own mouse tracking to isolate state changes
// and prevent re-renders of the entire chart when mouse position changes.

interface MemoizedTooltipContentProps {
  // Our props
  tooltipData: any;
  showPercent: boolean;
  vizWrapperRef: React.RefObject<HTMLDivElement>;
  levelNames?: string[];
}

const MemoizedTooltipContentInner: React.FC<MemoizedTooltipContentProps> = ({
  tooltipData,
  showPercent,
  vizWrapperRef,
  levelNames,
}) => {
  // Mouse tracking is isolated here - state changes don't propagate to parent
  const { mousePosition, isHovering } = useTrackMousePosition({
    containerRef: vizWrapperRef,
  });

  // Don't render tooltip when not hovering or no tooltip data
  if (!isHovering || mousePosition === null || !tooltipData) {
    return null;
  }

  // Get level name from levelNames array (for sunburst charts)
  const level = tooltipData.labels?.level;
  const levelName = Array.isArray(levelNames) && level !== undefined && level >= 0
    ? levelNames[level]
    : undefined;

  return (
    <TooltipV2
      controlled
      isOpen={isHovering && mousePosition !== null}
      mousePosition={mousePosition}
      primaryContent={{
        layout: 'custom',
        render: (
          <TooltipContent
            type="pie"
            data={tooltipData.labels}
            showPercent={showPercent}
            levelName={levelName}
          />
        ),
      }}
    />
  );
};

const MemoizedTooltipContent = React.memo(MemoizedTooltipContentInner);

// Static color: color-mix breaks html2canvas (widget image download).
const OTHERS_COLOR = "#ECECEC";

// Shared with the report widget's config form (re-exported via WidgetPieChart/constants).
export const DEFAULT_MAX_SEGMENTS = 12;
export const OTHER_SEGMENT = "Others";

const setPath = (obj: any, path: string, val: any) => {
  const keys = path.split(".");
  let cur = obj;
  keys.slice(0, -1).forEach((k) => {
    cur[k] = cur[k] || {};
    cur = cur[k];
  });
  cur[keys[keys.length - 1]] = val;
  return obj;
};

// Keep the largest `n` segments and sum the rest into one "Others" slice
// (isOthers → rendered last and grey). Grouped segments ride along on
// `othersSegments` so the tooltip can list them; percentages are against the
// full chart total, matching the other slices.
const groupSmallestIntoOther = (
  data: any[],
  n: number,
  valuePath: string,
  labelPath: string
) => {
  if (!Array.isArray(data) || n >= data.length) return data;
  const count = (d: any) => getVal(d, valuePath) || 0;
  const sorted = [...data].sort((a, b) => count(b) - count(a));
  const grouped = sorted.slice(n);
  const otherCount = grouped.reduce((sum, d) => sum + count(d), 0);
  const total = sorted.reduce((sum, d) => sum + count(d), 0);
  const othersDatum = {
    value: OTHER_SEGMENT,
    isOthers: true,
    othersSegments: grouped.map((d) => ({
      display: getVal(d, labelPath) ?? d.value,
      count: count(d),
      percentage: total ? (count(d) / total) * 100 : 0,
    })),
  };
  setPath(othersDatum, labelPath, OTHER_SEGMENT);
  setPath(othersDatum, valuePath, otherCount);
  return [...sorted.slice(0, n), othersDatum];
};

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
  payload,
}: any) => {
  const config = _base_piechart_ui_configs;
  
  // Only show label if percentage is above threshold
  if (percent < config.labelMinPercentThreshold) return null;
  
  // Don't show "0%" labels
  const percentage = percent * 100;
  if (percentage < 0.5) return null;

  // Calculate position using config ratio
  const radius = innerRadius + (outerRadius - innerRadius) * config.labelPositionRatio;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      key={`label-${index}`}
      x={x}
      y={y}
      fill={colors2.black}
      textAnchor="middle"
      dominantBaseline="central"
      className={config.labelCssClass}
      style={{ pointerEvents: "none" }}
    >
      {`${percentage.toFixed(0)}%`}
    </text>
  );
};

const renderActiveShape = (
  props: any,
  activeIndex?: number,
  currentIndex?: number
) => {
  const {
    cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill,
    // Set on the Cell when this slice's panel is open.
    stroke, strokeWidth,
  } = props;
  const config = _base_piechart_ui_configs;

  const isActive = activeIndex === currentIndex;
  const growthAmount = isActive ? config.activeSliceGrowth : 0;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + config.activeShapeOuterRadiusIncrease + growthAmount}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        stroke={stroke || colors2.grey30}
        strokeWidth={stroke ? strokeWidth ?? 1.5 : 1}
        style={{
          transition: config.activeShapeTransitionDuration,
        }}
      />
    </g>
  );
};

export interface PieChartProps {
  data: any;
  valuePath: string | string[];
  tooltipLabelsMapping?: { [key: string]: string };
  tooltipLabelsPath?: string | string[];
  breakdownCategoryName?: string;
  labelPath: string;
  title?: string;
  onClick?: ({}: any) => void;
  // Body / sub-header of the datapoint detail panel. No renderDatumDetails
  // means no panel: the chart knows nothing about what a slice contains.
  renderDatumDetails?: (datum: any) => React.ReactNode;
  renderDatumSubHeader?: (datum: any) => React.ReactNode;
  colors?: string[];
  colorIdx?: number;
  plotDataPoint?: "percentage" | "count";
  showPercent?: boolean;
  showCount?: boolean;
  innerLabel?: string;
  style?: CSSProperties;
  hoverDelay?: number;
  levelNames?: string[];
  // Combine the smallest slices into a single grey "Others" slice. OFF by
  // default; flat single-level pies only (ignored for breakdown/multi-level data).
  combineSegments?: boolean;
  // Slices to keep before grouping. Defaults to min(DEFAULT_MAX_SEGMENTS, total).
  numberOfSegments?: number;
}

// A slice's bucket id; the name only stands in for data without ids.
const sliceKey = (labels: any) => String(labels?.value ?? labels?.name);

export const PieChart: React.FunctionComponent<PieChartProps> = ({
  data: _data,
  valuePath,
  tooltipLabelsMapping,
  tooltipLabelsPath,
  breakdownCategoryName,
  labelPath = "name",
  onClick,
  renderDatumDetails,
  renderDatumSubHeader,
  colors = dataVizColors,
  colorIdx = 0,
  showPercent = true,
  style,
  plotDataPoint = "percentage",
  showCount = false,
  hoverDelay = 0,
  innerLabel,
  levelNames,
  combineSegments = false,
  numberOfSegments,
}) => {
  const [tooltipData, setTooltipData] = useState<any>(null);
  const [tooltipLevel, setTooltipLevel] = useState<number | null>(null);
  const [activeIndices, setActiveIndices] = useState<{ [level: number]: number | undefined }>({});
  const isBreakdown = Array.isArray(valuePath);

  // Flat pies only: breakdown (array valuePath) and multi-level (children)
  // data always render every segment.
  const combinedData = useMemo(() => {
    if (!combineSegments || isBreakdown || !Array.isArray(_data)) return _data;
    if (_data.some((d: any) => d?.children?.length)) return _data;
    const n = numberOfSegments ?? Math.min(DEFAULT_MAX_SEGMENTS, _data.length);
    return groupSmallestIntoOther(_data, n, valuePath as string, labelPath);
  }, [_data, combineSegments, numberOfSegments, isBreakdown, valuePath, labelPath]);

  const { data: barChartData } = usePrepareData({
    _data: combinedData,
    labelPath,
    valuePath,
    tooltipLabelsMapping,
    tooltipLabelsPath,
    isBreakdown,
    showPercent,
    summarizeAfterIdx: undefined, // PieChart doesn't use summarization
    colorIdx,
  });

  // Adapt the BarChart data format to PieChart format
  const pieData = usePieChartData({
    barChartData,
    isBreakdown,
    rawData: combinedData,
    labelPath,
    valuePath,
  });

  // Check if it's multi-level data
  const isMultiLevel = useMemo(() => {
    return (
      pieData &&
      typeof pieData === "object" &&
      "isMultiLevel" in pieData &&
      (pieData as MultiLevelPieData).isMultiLevel
    );
  }, [pieData]);

  const multiLevelData = isMultiLevel ? (pieData as MultiLevelPieData) : null;
  const singleLevelData = !isMultiLevel ? (pieData as any[]) : [];

  // Get number of levels and level data
  const numLevels = useMemo(() => {
    if (!multiLevelData || !multiLevelData.levelData) {
      return isMultiLevel ? 2 : 1; // Fallback to 2 for backward compatibility
    }
    return (multiLevelData.maxLevel || 0) + 1; // maxLevel is 0-based, so +1
  }, [multiLevelData, isMultiLevel]);

  // Generate colors for all levels
  const levelColors = useMemo(() => {
    if (!multiLevelData || !multiLevelData.levelData) {
      // Fallback for backward compatibility
      const parentColors = multiLevelData?.parentData?.map(
        (_, index) => colors[(index + colorIdx) % colors.length]
      ) || [];
      
      const childColorsByParent: { [key: number]: string[] } = {};
      if (multiLevelData) {
    multiLevelData.parentData.forEach((parent, parentIndex) => {
      const children = multiLevelData.childDataByParent[parentIndex] || [];
          const parentColor = parentColors[parentIndex];
          childColorsByParent[parentIndex] = generateChildColors(parentColor, children, 1);
      });
      }
      
      return {
        0: parentColors.reduce((acc, color, idx) => ({ ...acc, [idx]: color }), {}),
        1: childColorsByParent,
      };
    }
    
    // Generate base colors for level 0
    const baseColors = (multiLevelData.levelData[0] || []).map(
      (_, index) => colors[(index + colorIdx) % colors.length]
    );
    
    return generateColorsForAllLevels(baseColors, multiLevelData.levelData, multiLevelData.maxLevel || 0);
  }, [multiLevelData, colors, colorIdx]);

  // Calculate radius ranges for all levels
  const radiusRanges = useMemo(() => {
    return calculateRadiusRanges(numLevels);
  }, [numLevels]);

  const data = isMultiLevel ? (multiLevelData!.levelData?.[0] || multiLevelData!.parentData) : singleLevelData;

  // Legend: Show only Level 1 (top level)
  const legendItems = useMemo(
    () => {
      const level0Data = isMultiLevel && multiLevelData?.levelData?.[0]
        ? multiLevelData.levelData[0]
        : data;
      return level0Data.map((d) => ({
        text: d.labels.tooltipLabel || d.labels.name,
      }));
    },
    [data, isMultiLevel, multiLevelData]
  );

  const { vizWrapperRef, orientation, shouldShowLegend } = useLegendOrientation(
    {
      widthThreshold: 550,
      chartType: "pie",
      legendItems,
    }
  );

  // NOTE: Mouse tracking has been moved to MemoizedTooltipContent to isolate
  // state changes and prevent re-renders of the entire chart on mouse movement.

  // Use sizing hook for single-level charts (responsive)
  const { innerRadius: calculatedInnerRadius, outerRadius: calculatedOuterRadius } = usePieChartSizing({
    containerRef: vizWrapperRef,
    shouldShowLegend,
    orientation,
  });

  // Generic handlers for any level
  const onPieEnter = useCallback((level: number, colorMap: { [index: number]: string }) => (data: any, index: number) => {
    setActiveIndices((prev) => ({ ...prev, [level]: index }));
    // Include barColor in tooltip data from the levelColors. The combined "Others"
    // slice is always grey (read from the hovered datum, not the index-keyed map).
    const isOthers = data?.payload?.labels?.isOthers;
    const barColor = isOthers
      ? OTHERS_COLOR
      : colorMap[index] || colors[(index + colorIdx) % colors.length];

    // The segments grouped into "Others" get the palette colour they would have
    // had if they'd been rendered as their own slice. The Others slice sits at
    // `index`, i.e. exactly where the first grouped segment would have been, so
    // they continue the palette from there.
    const othersSegments = isOthers && Array.isArray(data.payload.labels.othersSegments)
      ? data.payload.labels.othersSegments.map((seg: any, i: number) => ({
          ...seg,
          barColor: colors[(index + i + colorIdx) % colors.length],
        }))
      : undefined;

    setTooltipData({
      ...data.payload,
      labels: {
        ...data.payload.labels,
        barColor,
        ...(othersSegments ? { othersSegments } : {}),
      },
    });
    setTooltipLevel(level);
  }, [colors, colorIdx]);

  const onPieLeave = useCallback((level: number) => () => {
    setActiveIndices((prev) => {
      const newIndices = { ...prev };
      delete newIndices[level];
      // Only clear tooltip if no other level is active
      if (Object.keys(newIndices).length === 0) {
      setTooltipData(null);
      setTooltipLevel(null);
    }
      return newIndices;
    });
  }, []);

  // Only the flat pie is drillable (the donut's inner levels carry no bucket
  // ids). The slice is kept by its bucket id and read from the current data,
  // so a re-sort can't move the mark or the panel onto another slice.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selectedIndex = selectedKey
    ? data.findIndex((d: any) => sliceKey(d?.labels) === selectedKey)
    : -1;
  const selected = selectedIndex === -1 ? null : data[selectedIndex].labels;
  useEffect(() => {
    if (selectedKey && !selected) setSelectedKey(null);
  }, [selectedKey, selected]);

  const onPieClick = useCallback((level: number, levelData: any[]) => (_: any, index: number) => {
    // "Others" isn't a real datum — click-to-filter on it matches nothing.
    if (levelData[index]?.labels?.isOthers || !levelData[index]) return;
    const labels = { ...levelData[index].labels };
    onClick?.(labels);
    if (renderDatumDetails && level === 0) setSelectedKey(sliceKey(labels));
  }, [onClick, renderDatumDetails]);

  const handleMouseLeave = useCallback(() => {
    setActiveIndices({});
    setTooltipData(null);
    setTooltipLevel(null);
  }, []);

  // ===========================================================================
  // PERFORMANCE OPTIMIZATION: Pre-compute tooltip content element
  // ===========================================================================
  // Creates a stable React element for the tooltip.
  // Avoids inline function (ESLint: react-perf/jsx-no-new-function-as-prop).
  // Mouse tracking is handled inside MemoizedTooltipContent to isolate state changes.
  const tooltipContentElement = useMemo(
    () => (
      <MemoizedTooltipContent
        tooltipData={tooltipData}
        showPercent={showPercent}
        vizWrapperRef={vizWrapperRef}
        levelNames={levelNames}
      />
    ),
    [tooltipData, showPercent, vizWrapperRef, levelNames]
  );

  // Calculate radius for charts
  const config = _base_piechart_ui_configs;
  const containerHeight = style?.height || style?.maxHeight || config.defaultContainerHeight;

  // Determine if we should render as single level
  const shouldRenderAsSingleLevel = !isMultiLevel || numLevels === 1 ||
    (multiLevelData && (!multiLevelData.levelData || Object.keys(multiLevelData.levelData).length === 1));

  // Prepare legend data - only Level 1 (top level)
  const legendData = useMemo(() => {
    if (isMultiLevel && multiLevelData) {
      const level0Data = multiLevelData.levelData?.[0] || multiLevelData.parentData;
      return level0Data.map((d, index) => ({
          text: d.labels.tooltipLabel || d.labels.name,
        color: levelColors[0]?.[index] || colors[(index + colorIdx) % colors.length],
        }));
      }
    return data.map((d, index) => ({
      text: d.labels.tooltipLabel || d.labels.name,
      color: d.labels?.isOthers ? OTHERS_COLOR : colors[(index + colorIdx) % colors.length],
    }));
  }, [isMultiLevel, multiLevelData, data, colors, colorIdx, levelColors]);

  // Handle empty data (after all hooks to preserve hook ordering)
  if (data.length === 0) return <div>No data available</div>;

  return (
    <StyledPieChartContainer
      className="pieChartCompContainer"
      ref={vizWrapperRef}
      style={{
        ...style,
        overflow: "hidden",
        height: containerHeight,
        position: "relative",
      }}
      onMouseLeave={handleMouseLeave}
    >
      {/* Tooltip is always rendered; mouse tracking & visibility handled internally */}
      {tooltipData && tooltipLevel !== null && tooltipContentElement}

      <VizShell orientation={orientation} fillHeight={true}>
        <VizArea style={{ display: "flex", alignItems: "center", paddingTop: "0" }}>
          <ResponsiveContainer
            width={"100%"}
            height={"100%"}
            style={{ overflow: "hidden" }}
          >
            <ReChartsPie>
              {/* Required in v3 for internal active-slice tracking that drives activeShape.
                  content={() => null} renders nothing; cursor={false} hides the cursor indicator.
                  Our own tooltip (MemoizedTooltipContent) continues to handle display. */}
              <Tooltip content={() => null} cursor={false} />
              {shouldRenderAsSingleLevel ? (
                /* Single-level pie */
                <Pie
                  data={data}
                  cx={"50%"}
                  cy={"50%"}
                  startAngle={90}
                  endAngle={-270}
                  label={renderCustomizedLabel}
                  fill="#8884d8"
                  dataKey="labels.count"
                  nameKey="labels.name"
                  innerRadius={calculatedInnerRadius}
                  outerRadius={calculatedOuterRadius}
                  labelLine={false}
                  isAnimationActive={false}
                  activeShape={renderActiveShape}
                  onMouseEnter={onPieEnter(0, data.reduce((acc, _, idx) => ({ ...acc, [idx]: colors[(idx + colorIdx) % colors.length] }), {}))}
                  onMouseLeave={onPieLeave(0)}
                  onClick={onClick || renderDatumDetails ? onPieClick(0, data) : undefined}
                  style={{ cursor: "pointer" }}
                >
                  {data.map((entry, index) => {
                    const isActive = activeIndices[0] === index;
                    const shouldDim = activeIndices[0] !== undefined && !isActive;
                    // The combined "Others" slice always renders grey. Uses the raw
                    // translucent token (SVG fill is unreliable with color-mix); on the
                    // white chart it matches OTHERS_COLOR used for the CSS swatches.
                    const fillColor = entry?.labels?.isOthers
                      ? colors2.grey15
                      : colors[(index + colorIdx) % colors.length];

                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={fillColor}
                        opacity={shouldDim ? config.dimmedOpacity : 1}
                        // Selected = this slice's panel is open. Same
                        // persistent outline a selected bar gets.
                        stroke={selectedIndex === index ? colors2.grey80 : undefined}
                        strokeWidth={selectedIndex === index ? 1.5 : undefined}
                        style={{
                          transition: config.cellTransitionDuration,
                        }}
                      />
                    );
                  })}
                </Pie>
              ) : (
                /* Multi-level pie - render N levels */
                <>
                  {Array.from({ length: numLevels }, (_, level) => {
                    const levelData = multiLevelData?.levelData?.[level];
                    if (!levelData || levelData.length === 0) return null;
                    
                    const radiusRange = radiusRanges[level];
                    const levelColorMap = levelColors[level] || {};
                    
                    // Determine if any other level is active for dimming
                    const otherLevelsActive = Object.keys(activeIndices).some(
                      (key) => parseInt(key) !== level && activeIndices[parseInt(key)] !== undefined
                    );
                    
                    return (
                      <Pie
                        key={`pie-level-${level}`}
                        data={levelData}
                        cx={"50%"}
                        cy={"50%"}
                        startAngle={90}
                        endAngle={-270}
                        label={level === 0 ? renderCustomizedLabel : undefined}
                        fill="#8884d8"
                        dataKey="labels.count"
                        nameKey="labels.name"
                        innerRadius={radiusRange.innerRadius}
                        outerRadius={radiusRange.outerRadius}
                        labelLine={false}
                        isAnimationActive={false}
                        
                        onMouseEnter={onPieEnter(level, levelColorMap)}
                        onMouseLeave={onPieLeave(level)}
                        onClick={onClick || renderDatumDetails ? onPieClick(level, levelData) : undefined}
                        style={{ cursor: "pointer" }}
                      >
                        {levelData.map((entry, index) => {
                          const isActive = activeIndices[level] === index;
                          const shouldDim =
                            (activeIndices[level] !== undefined && !isActive) ||
                            otherLevelsActive;
                          const fillColor = levelColorMap[index] || colors[(index + colorIdx) % colors.length];

                          return (
                            <Cell
                              key={`cell-level-${level}-index-${index}`}
                              fill={fillColor}
                              opacity={shouldDim ? config.dimmedOpacity : 1}
                              style={{
                                transition: config.cellTransitionDuration,
                              }}
                            />
                          );
                        })}
                      </Pie>
                    );
                  })}
                </>
              )}
            </ReChartsPie>
          </ResponsiveContainer>
        </VizArea>

        {shouldShowLegend && (
          <VizPanel position={orientation === "vertical" ? "right" : "bottom"}>
            <Legend orientation={orientation} data={legendData} />
          </VizPanel>
        )}
      </VizShell>
      <SidePanel
        isOpen={!!selected}
        onClose={() => setSelectedKey(null)}
        title={selected && (selected.tooltipLabel || selected.name)}
        subHeader={selected && renderDatumSubHeader?.(selected)}
        closeOnOutsideClick={false}
        exclusiveGroup="datumDetail"
        contentKey={selectedKey}
      >
        {selected && renderDatumDetails(selected)}
      </SidePanel>
    </StyledPieChartContainer>
  );
};
